'use client';

import {Button} from '@purpose/ui/components/button';
import {ImageWithFallback} from '@purpose/ui/components/ImageWithFallback';
import {useResponsive} from '@purpose/ui/hooks/useResponsive';
import {cn} from '@purpose/ui/lib/utils';
import {PlusIcon} from 'lucide-react';

interface CategoryCardProps {
  width?: number | string;
  image: string;
  title?: string;
  description?: string;
  subtitle?: string;
  buttonTitle: string;
  onButtonClick?: () => void;
  onClick?: () => void;
  toggled?: boolean;
  icon?: React.ReactNode;
  isLoading?: boolean;
  row?: boolean;
  fallbackWrapperClassName?: string;
}

const CategoryCard = ({
  width,
  image,
  title,
  subtitle,
  description,
  buttonTitle,
  onButtonClick,
  onClick,
  toggled,
  icon,
  isLoading,
  row,
  fallbackWrapperClassName
}: CategoryCardProps) => {
  const {isXs, isSm, isMd, isLg, isMobile} = useResponsive();

  const imageHeight = (() => {
    if (isXs) {
      return 188;
    } else if (isSm) {
      return 188;
    } else if (isMd) {
      return 188;
    } else if (isLg) {
      return 198;
    } else {
      return 205;
    }
  })();

  return (
    <div
      className="card-container"
      style={{
        width: '100%',
        borderRadius: '16px',
        cursor: 'pointer',
        display: 'flex',
        gap: '4px',
        overflow: 'hidden',
        position: 'relative',
        justifyContent: 'space-between',
        maxHeight: '402px',
        backgroundColor: '#fafafaff',
        padding: '16px 12px'
      }}
    >
      <div
        style={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          gap: '16px',
          maxWidth: '100%',
          maxHeight: '100%',
          height: 'fit-content'
        }}
      >
        <div
          style={{
            flex: 1,
            display: 'flex',
            gap: '16px',
            flexDirection: row ? 'row' : 'column'
          }}
        >
          <ImageWithFallback
            {...(onClick && {onClick})}
            className="card-image"
            fallbackWrapperClassName={cn(
              'rounded-full',
              fallbackWrapperClassName
            )}
            width={row ? 86 : '100%'}
            height={row ? 86 : '100%'}
            containerStyle={{
              minHeight: row ? 86 : imageHeight,
              minWidth: row ? 86 : imageHeight
            }}
            src={image}
            alt={title || 'card image'}
            priority
            loading="eager"
            placeholder="empty"
            quality={85}
            sizes={`${row ? 86 : imageHeight}px`}
            aspectRatio={1}
            fill
            style={{
              borderRadius: '50%',
              margin: 'auto',
              minHeight: row ? 0 : imageHeight,
              minWidth: row ? 86 : imageHeight,
              ...(row && {
                minHeight: 0,
                maxHeight: 86,
                width: 86,
                maxWidth: 86
              })
            }}
            iconStyles={{
              width: row ? 48 : 124,
              height: row ? 48 : 124
            }}
          />
          <div
            style={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              gap: '4px'
            }}
          >
            {title && (
              <h3
                {...(onClick && {onClick})}
                className="line-clamp-1"
                style={{
                  fontSize: '16px',
                  fontWeight: 600,
                  margin: 0,
                  lineHeight: '20px'
                }}
              >
                {title}
              </h3>
            )}
            {description && (
              <p
                className="line-clamp-1"
                style={{margin: 0, fontSize: '14px', color: '#666666ff'}}
              >
                {description}
              </p>
            )}
            {subtitle && (
              <p
                className="line-clamp-1"
                style={{fontSize: '14px', fontWeight: 600}}
              >
                {subtitle}
              </p>
            )}
          </div>
        </div>
        {toggled ? (
          <Button
            variant="outline"
            size="lg"
            style={{
              backgroundColor: '#eeeeeeff',
              marginTop: 'auto',
              height: isMobile ? '36px' : '44px',
              boxShadow: 'none',
              border: '1px solid #1f2937ff'
            }}
            {...(onButtonClick && {onClick: onButtonClick})}
            isLoading={isLoading}
            aria-label={buttonTitle}
            className="shrink-0 gap-2"
          >
            {icon || <PlusIcon className="color-primary-base" />}
            {buttonTitle}
          </Button>
        ) : (
          <Button
            variant="default"
            size="lg"
            onClick={onButtonClick}
            style={{marginTop: 'auto', height: isMobile ? '36px' : '44px'}}
            isLoading={isLoading}
            className="max-w-full shrink-0 gap-2"
            aria-label={buttonTitle}
          >
            {icon || <PlusIcon className="color-primary-base" />}
            {buttonTitle}
          </Button>
        )}
      </div>
    </div>
  );
};

export {CategoryCard};
export type {CategoryCardProps};
